import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Company, PrintoutImageType } from '@cl/shared/models/company.model';
import { BaseDownloadService } from './base.service';
import { lastValueFrom, Observable } from 'rxjs';
import { FileMetadata } from '@cl/shared/models/file.model';
import { NzImageService } from 'ng-zorro-antd/image';
import { CommandResult } from '@cl/shared/models';

@Injectable({ providedIn: 'root' })
export class CompaniesService extends BaseDownloadService<Company> {
    constructor(
        httpClient: HttpClient,
        protected nzImageService: NzImageService,
    ) {
        super(httpClient, '/api/v1/companies/', nzImageService);
    }

    async downloadAttachment(companyId: number, imageType: PrintoutImageType): Promise<void> {
        return this.handleFileDownloading(
            lastValueFrom(this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}${companyId}/attachments/${imageType}/`)),
        );
    }

    async deleteAttachment(companyId: number, imageType: PrintoutImageType): Promise<void> {
        return lastValueFrom(this.httpClient.delete<void>(`${this.baseApiUrl}${companyId}/attachments/${imageType}/`));
    }

    addAttachment(companyId: number, formData: FormData, imageType: PrintoutImageType): Promise<boolean> {
        return lastValueFrom(this.httpClient.post<boolean>(`${this.baseApiUrl}${companyId}/attachments/${imageType}`, formData));
    }

    // companyScopeOnly leaves out companies reached through a single department's VIEW right.
    getAccessibleCompanies(activeOnly = false, companyScopeOnly = false): Observable<CommandResult<Company[]>> {
        const params = new HttpParams().set('activeOnly', activeOnly).set('companyScopeOnly', companyScopeOnly);
        return this.httpClient.get<CommandResult<Company[]>>(`${this.baseApiUrl}accessible`, { params });
    }

    getAuthorizedCompanies(permissionId: number): Observable<CommandResult<Company[]>> {
        return this.httpClient.get<CommandResult<Company[]>>(`${this.baseApiUrl}authorized/${permissionId}`);
    }

    getUserCompanies(withBookkeeperCheck: boolean = false): Observable<CommandResult<Company[]>> {
        return this.httpClient.get<CommandResult<Company[]>>(`${this.baseApiUrl}user-companies/${withBookkeeperCheck}`);
    }

    async getDepartmentCompany(departmentId: number): Promise<CommandResult<Company>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<Company>>(`${this.baseApiUrl}by-department/${departmentId}`));
    }

    toString(company: Company): string {
        return company.name;
    }
}
